import Locale from '../../mixins/locale';
import Emitter from '../../mixins/emitter';
import Navigation from './navigation-mixin';
import { valueEquals, getValueByPath, arrayFind } from '../../utils/util';
import scrollbarWidth from '../../utils/scrollbar-width';
import scrollIntoView from '../../utils/scroll-into-view';
import { addClass, removeClass, hasClass } from '../../utils/dom';
import Clickoutside from '../../utils/clickoutside';
import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
import ZmInput from '../input';
import ZmSelectDropdown from './select-dropdown.vue';
import ZmOption from './option.vue';
import ZmTag from '../tag';
import ZmTagGroup from '../tag-group/tag-group';
import ZmScrollbar from '../scrollbar';
import ZmCheckboxList from '../checkbox-list';
import { isWin, isMobie, isSafari } from '../../utils/env';
import AriaUtils from '../../utils/aria-utils';

let _uid = 0;

function uid() {
  return _uid++;
}

export default {
  mixins: [Locale, Emitter, Navigation],
  provide() {
    return {
      'select': this
    };
  },
  inject: {
    ZmForm: {
      default: null
    },
    ZmFormItem: {
      default: null
    }
  },
  directives: { Clickoutside },
  components: {
    ZmInput,
    ZmSelectDropdown,
    ZmOption,
    ZmTag,
    ZmTagGroup,
    ZmScrollbar,
    ZmCheckboxList
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    value: [String, Number, Array, Object, Boolean],
    disabled: Boolean,
    placeholder: String,
    valueKey: {
      type: String,
      default: 'value'
    },
    widthLimit: [Boolean, Object],
    multiple: Boolean,
    multipleLimit: {
      type: Number,
      default: 0
    },
    label: String,
    labelBy: String,
    listLabel: String,
    listLabelBy: String,
    ariaRequired: Boolean,
    size: {
      type: String,
      default: 'small'
    },
    collapseTags: Boolean,
    clearable: Boolean,
    popperAppendToBody: Boolean,
    popperOptions: {
      type: Object
    },
    selectOptions: {
      type: Array
    },
    selectOptionsValueKey: {
      type: String,
      default: 'value'
    },
    maxLine: {
      type: Number,
      default: 0
    },
    inlineLabel: String,
    popperClass: String,
    filterLabel: Function
  },
  data() {
    return {
      visible: false,
      options: [],
      optionsCount: 0,
      cachedOptions: [],
      selectedLabel: '',
      currentPlaceholder: '',
      cachedPlaceHolder: '',
      isInputFocus: false,
      inputWidth: 0,
      selected: this.multiple ? [] : {},
      sortingOptions: false,
      hoverIndex: -1,
      duration: isWin || isMobie ? 20 : 300,
      isSilentBlur: false,
      wrapStyl: ''
    };
  },
  computed: {
    targetId() {
      return this.id ? this.id : 'select-' + uid();
    },
    listId() {
      return `${this.targetId}-popup-list`;
    },
    elFormItemSize() {
      return (this.ZmFormItem || {}).elFormItemSize;
    },
    isInlineLabel() {
      return this.inlineLabel || (this.ZmForm && this.ZmForm.labelPosition === 'inline');
    },
    inlineLabelText() {
      if (this.ZmFormItem && this.ZmFormItem.label) {
        return this.ZmFormItem.label;
      } else {
        return this.inlineLabel;
      }
    },
    labelFor() {
      return (this.ZmFormItem && this.ZmFormItem.labelFor) || this.id;
    },
    selectSize() {
      return this.isInlineLabel ? 'xLarge' : (this.elFormItemSize || this.size);
    },
    selectDisabled() {
      return this.disabled || (this.ZmForm || {}).disabled;
    },
    isRequired() {
      return this.ariaRequired || (this.ZmFormItem || {}).isRequired;
    },
    collapseTagSize() {
      return 'small';
      /* return ['small'].indexOf(this.selectSize) > -1
        ? 'small'
        : 'middle';*/
    },
    appendCls() {
      const cls = [];
      const selectSize = this.selectSize;
      if (selectSize) {
        cls.push(`zm-select--${selectSize}`);
      }
      if (this.widthLimit) {
        cls.push('zm-select-width-limit');
      }
      if (this.visible || this.isInputFocus) {
        cls.push('is-focus');
      }
      if (this.selectDisabled) {
        cls.push('is-disabled');
      }
      return cls.join(' ');
    },
    showClear() {
      return this.clearable &&
        !this.selectDisabled &&
        !this.multiple &&
        this.value !== undefined &&
        this.value !== null &&
        this.value !== '';
    },
    toggleText() {
      if (this.visible) {
        return this.t('select.hideOptions');
      } else {
        return this.t('select.showOptions');
      }
    },
    inputWrapStyl() {
      let style = 'height:auto;';
      const maxLine = this.maxLine;
      if (maxLine > 0) {
        style += `max-height:${(maxLine * 30)}px;`;
      }
      return style;
    },
    inputStyl() {
      return this.showClear ? 'padding-right: 45px' : '';
    },
    shouldShowOptions() {
      return this.options.length > 0 && (!this.loading || (this.loading && this.hasMore));
    },
    inlineFocus() {
      if (!this.isInlineLabel) {
        return false;
      }
      return this.visible && this.empty || !this.empty;
    },
    isFilterableSelect() {
      return this.$options.name === 'FilterableSelect';
    }
  },
  watch: {
    placeholder(newVal) {
      this.cachedPlaceHolder = this.currentPlaceholder = newVal;
    },
    selectOptions() {
      this.sortingOptions = true;
      this.$nextTick(() => {
        this.sortOptions();
        this.sortingOptions = false;
      });
    }
  },
  created() {
    this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder;

    this.$on('handleOptionClick', this.handleOptionSelect);
    this.$on('setSelected', this.setSelected);
    this.$on('handleOptionMouseDown', this.handleOptionMouseDown);

    this.wrapStyl = this.getWrapStyl();
  },
  mounted() {
    if (this.multiple && Array.isArray(this.value) && this.value.length > 0) {
      this.currentPlaceholder = '';
    }
    addResizeListener(this.$el, this.handleResize);
    if (this.value) {
      this.activedescendant = this.value;
    }
    const reference = this.$refs.reference;
    if (reference) {
      this.$nextTick(() => {
        this.inputWidth = reference.getBoundingClientRect().width;
      });
    }
    this.setSelected();
    this.$nextTick(() => {
      if (this.multiple && !Array.isArray(this.value)) {
        this.$emit('input', []);
      }
      if (!this.multiple && Array.isArray(this.value)) {
        this.$emit('input', '');
      }
    });
  },
  beforeDestroy() {
    if (this.$el && this.handleResize) {
      removeResizeListener(this.$el, this.handleResize);
    }
    this.handleMenuLeave();
  },
  methods: {
    hasTabbableSlot() {
      const appendSlots = this.$slots['popper-append'] || [];
      const appendEl = appendSlots[0] && appendSlots[0].elm;
      return AriaUtils.getFocusableChildElements(appendEl, false, true).length;
    },
    toggleMenu() {
      if (!this.selectDisabled) {
        this.visible = !this.visible;
      }
    },
    handleClose() {
      this.visible = false;
    },
    handleFocus(event) {
      this.isInputFocus = true;
      this.$emit('focus', event);
    },
    handleBlur(event) {
      this.isInputFocus = false;
      if (this.emptyText) {
        this.visible = false;
      }
      if (this.isSilentBlur) {
        this.isSilentBlur = false;
      } else {
        this.$emit('blur', event);
        this.dispatch('ZmFormItem', 'el.form.blur');
      }
    },
    deleteTag(event, tag) {
      const index = this.selected.indexOf(tag);
      if (index > -1 && !this.selectDisabled) {
        const value = this.value.slice();
        value.splice(index, 1);
        this.$emit('input', value);
        this.emitChange(value);
        this.$emit('remove-tag', tag.value);
      }
      this.focus();
      event.stopPropagation();
    },
    clearSelect(e) {
      this.deleteSelected(e);
      this.focus();
    },
    deleteSelected(event) {
      event.stopPropagation();
      this.$emit('input', '');
      this.emitChange('');
      this.visible = false;
      this.$emit('clear');
    },
    emitChange(val) {
      if (!valueEquals(this.value, val)) {
        this.$emit('change', val);
      }
    },
    getValueKey(item) {
      if (Object.prototype.toString.call(item.value).toLowerCase() !== '[object object]') {
        return item.value;
      } else {
        return getValueByPath(item.value, this.valueKey);
      }
    },
    handleIconClick() {
      this.visible = !this.visible;
      if (this.visible) {
        this.focus();
      }
    },
    handleIconHide() {
      let icon = this.$el.querySelector('.zm-input__icon');
      if (icon) {
        removeClass(icon, 'is-reverse');
      }
    },
    handleIconShow() {
      let icon = this.$el.querySelector('.zm-input__icon');
      if (icon && !hasClass(icon, 'zm-icon-outline-error')) {
        addClass(icon, 'is-reverse');
      }
    },
    handleMenuEnter() {
      this.$nextTick(() => {
        let selectedOptions = this.selected;
        if (this.multiple) {
          selectedOptions = this.selected.map(({ value }) => this.getOption(value));
        }
        this.scrollToOptionMiddle(selectedOptions);
        if (this.isFilterableSelect && this.multiple && !this.selected.length) {
          this.resetScrollbarPosition();
        }
        if (isWin) {
          // fix windows wired behavior
          // tree select has no scrollbar component
          this.$refs.scrollbar && this.$refs.scrollbar.refreshWrapStyl();
        }
      });
    },
    handleMenuLeave() {
      this.hoverIndex = -1;
    },
    scrollToOptionMiddle(option) {
      const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el;
      if (this.$refs.popper && target) {
        this.$refs.scrollbar.scrollIntoView(target, null, 0, -24);
      }
    },
    scrollToOption(option) {
      const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el;
      if (this.$refs.popper && target) {
        const menu = this.$refs.popper.$el.querySelector('.zm-select-dropdown__wrap');
        scrollIntoView(menu, target);
        this.$refs.scrollbar.handleScroll();
      }
    },
    getValueIndex(arr = [], value) {
      const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
      if (!isObject) {
        return arr.indexOf(value);
      } else {
        const valueKey = this.valueKey;
        let index = -1;
        arr.some((item, i) => {
          // unlike element-ui, zoom-ui can not unselect  SelectedOption
          /* istanbul ignore next */
          if (getValueByPath(item, valueKey) === getValueByPath(value, valueKey)) {
            index = i;
            return true;
          }
          return false;
        });
        return index;
      }
    },
    getOption(value) {
      let option;
      const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
      const isNull = Object.prototype.toString.call(value).toLowerCase() === '[object null]';

      for (let i = this.cachedOptions.length - 1; i >= 0; i--) {
        const cachedOption = this.cachedOptions[i];
        const isEqual = isObject
          ? getValueByPath(cachedOption.value, this.valueKey) === getValueByPath(value, this.valueKey)
          : cachedOption.value === value;
        if (isEqual) {
          option = cachedOption;
          break;
        }
      }
      if (option) return option;
      const label = (!isObject && !isNull)
        ? value : '';
      let newOption = {
        value: value,
        currentLabel: label
      };
      if (this.multiple) {
        newOption.hitState = false;
      }
      return newOption;
    },
    handleOptionSelect(option) {
      if (this.multiple) {
        const value = this.value.slice();
        const optionIndex = this.getValueIndex(value, option.value);
        if (optionIndex === -1 && (this.multipleLimit <= 0 || value.length < this.multipleLimit)) {
          value.push(option.value);
        }
        this.$emit('input', value);
        this.emitChange(value);
        if (option.created) {
          this.query = '';
          this.handleQueryChange('');
          this.inputLength = 20;
        }
      } else {
        this.$emit('input', option.value);
        this.emitChange(option.value);
        this.visible = false;
      }
      this.activedescendant = option.optionId;
      this.isSilentBlur = false;
      this.$nextTick(() => {
        this.scrollToOption(option);
        this.focus();
      });
    },
    handleOptionMouseDown() {
      this.isSilentBlur = true;
    },
    checkDefaultFirstOption() {
      this.hoverIndex = -1;
      // highlight the created option
      const options = this.options;
      this.$nextTick(() => {
        let hasCreated = false;
        for (let i = options.length - 1; i >= 0; i--) {
          if (options[i].created) {
            hasCreated = true;
            this.hoverIndex = i;
            break;
          }
        }
        if (hasCreated) {
          return;
        }
        for (let i = 0; i !== options.length; ++i) {
          const option = options[i];
          // highlight first options that passes the filter
          if (!option.computedDisabled && !option.groupDisabled && option.visible) {
            this.hoverIndex = i;
            break;
          }
        }
      });
    },
    resetHoverIndex() {
      if (this.multiple) {
        if (this.selected.length > 0 && this.isFilterableSelect) {
          this.hoverIndex = Math.min.apply(null, this.selected.map(item => this.options.some(option => option.value === item.value)));
        } else {
          this.hoverIndex = -1;
        }
      } else {
        this.hoverIndex = this.options.indexOf(this.selected);
      }
    },
    sortOptions() {
      const options = [];
      this.selectOptions.forEach((option) => {
        options.push(arrayFind(this.options, (o) => {
          return o.value === option[this.selectOptionsValueKey];
        }));
      });
      this.options = options;
    },
    handleResize() {
      this.resetInputWidth();
      this.forceUpdateScrollbar();
      if (this.multiple) {
        this.resetInputHeight();
      }
    },
    resetInputWidth() {
      this.inputWidth = this.$refs.reference.getBoundingClientRect().width;
    },
    selectOption() {
      if (!this.visible) {
        this.visible = true;
      } else {
        if (this.options[this.hoverIndex]) {
          this.handleOptionSelect(this.options[this.hoverIndex]);
        }
      }
    },
    handleTab(event) {
      if (this.hasTabbableSlot() && !event.shiftKey) {
        event.stopPropagation();
        return;
      }
      if (!event.shiftKey) {
        this.selectOption();
      }
      this.visible = false;
    },
    handleEsc() {
      this.visible = false;
      this.$nextTick(() => {
        this.focus();
      });
    },
    getWrapStyl(forceUpdate) {
      const gutter = scrollbarWidth(forceUpdate);
      const maxHeight = gutter ? '239px' : '224px';
      return `max-height:${maxHeight};`;
    },
    forceUpdateScrollbar() {
      this.wrapStyl = this.getWrapStyl(true);
      /* istanbul ignore next */
      if (isSafari) {
        this.fixScrollbarHeightCollapsed();
      }
    },
    fixScrollbarHeightCollapsed() {
      const scrollbar = this.$refs.wrapper;
      if (scrollbar) {
        scrollbar.$el.style.display = 'flex';
        setTimeout(() => {
          scrollbar.$el.style.display = '';
        }, 0);
      }
    },
    captureTabbableSlotKeydown(event) {
      if (this.hasTabbableSlot()) {
        this.tryClose(event, this.$options.name === 'NormalSelect');
      }
    },
    tryClose(event, isNormalSelect) {
      const appendSlots = this.$slots['popper-append'] || [];
      const appendEl = appendSlots[0] && appendSlots[0].elm;
      const keyCode = event.keyCode;
      const keys = AriaUtils.keys;
      if (keyCode === keys.esc) {
        this.handleEsc();
        event.preventDefault();
        event.stopPropagation();
      } else if (keyCode === keys.tab) {
        // when press tab or tab + shift
        // if focus is out of append slot in nextEventLoop, close it!
        setTimeout(() => {
          if (isNormalSelect) {
            // tab + shift key navigate from the first tabbable element to checkboxList
            if (this.multiple && this.$refs.checkboxList.$el.contains(document.activeElement) && event.shiftKey) return;
          }
          if (!appendEl.contains(document.activeElement)) {
            this.visible = false;
          }
        });
      }
    },
    doFilterLabel(...args) {
      return typeof this.filterLabel === 'function' ? this.filterLabel(...args) : args[0];
    }
  }
};
